import { Radio, RadioGroup } from "@blueprintjs/core";
import Select, { SingleValue } from "react-select";

import { Box } from "@/components/layout/flexbox";
import { ComponentMetadata } from "@/components/pages_logged_in/program/components/componentMetadata";
import { StyledFormGroup } from "@/components/pages_logged_in/program/components/set-components/StyledFormGroup";
import { SelectOption } from "@/types/select";

/**
 * Interface defining the props for ParticipantEligibilityFormInput component
 */
interface ParticipantEligibilityFormInputProps {
  componentMetadata: ComponentMetadata;
  disabled: boolean;
  isDependent: boolean;
  optionChange: (isDependent: boolean) => void;
  prerequisiteComponent?: SelectOption | null;
  programComponentOptions: SelectOption[];
  isValidEligibility: boolean;
  onProgramComponentChange: (value: SingleValue<SelectOption>) => void;
}

/**
 * Component for configuring participant eligibility settings
 * Allows setting whether participants are eligible immediately or after completing another component
 */
export function ParticipantEligibilityFormInput({
  componentMetadata,
  disabled,
  isDependent,
  optionChange,
  prerequisiteComponent,
  isValidEligibility,
  onProgramComponentChange,
  programComponentOptions,
}: ParticipantEligibilityFormInputProps) {
  /**
   * Handle change in dependency option
   */
  const handleDependencyOptionChange = (
    event: React.FormEvent<HTMLInputElement>,
  ) => {
    optionChange(event.currentTarget.value === "true");
  };

  return (
    <StyledFormGroup
      label="Participant Eligibility"
      subLabel={componentMetadata.participantEligibilityDesc}
    >
      <RadioGroup
        className="flex flex-col gap-3"
        disabled={disabled}
        selectedValue={isDependent ? "true" : "false"}
        onChange={handleDependencyOptionChange}
      >
        <Radio
          className="m-0"
          label="Eligible on component launch day"
          value={"false"}
        />
        <Radio
          className="m-0"
          label="Eligible after completing another component"
          value={"true"}
        />
      </RadioGroup>
      {isDependent && (
        <Box mt={2}>
          <Select
            value={prerequisiteComponent}
            onChange={onProgramComponentChange}
            isSearchable={true}
            options={programComponentOptions}
            closeMenuOnSelect={true}
            isDisabled={disabled}
          />
          <div className="text-sm text-subtext mt-2">
            Participants will become eligible for this component after
            completing the selected component.
          </div>
          {!isValidEligibility && !disabled && (
            <Box color={"#c23030"} fontSize={14} mt={10}>
              Please select a component
            </Box>
          )}
        </Box>
      )}
    </StyledFormGroup>
  );
}
